import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Users, Eye, TrendingUp, CalendarClock, Bot, ArrowUpRight, ArrowDownRight,
  Sparkles, Flame, Lightbulb, Pencil, X, Radio, BarChart3, Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { api } from '../api/client';

type Period = '7d' | '30d' | '90d';

interface SummaryCard {
  value: number | string;
  delta: number; // percent
}

interface AnalyticsSummary {
  channels_count: number;
  subscribers: SummaryCard;
  reach: SummaryCard;
  er: SummaryCard;
  scheduled: SummaryCard;
  farm: SummaryCard;
  chart: Array<{ date: string; subscribers: number }>;
}

interface UpcomingPost {
  id: string;
  channel_name: string;
  scheduled_at: string;
  text_preview: string;
}

interface DailyInsight {
  best_post: { title: string; description: string };
  trend: { title: string; description: string };
  recommendation: { title: string; description: string };
}

async function fetchSummary(period: Period): Promise<AnalyticsSummary> {
  const { data } = await api.get<AnalyticsSummary>('/analytics/summary', { params: { period } });
  return data;
}

async function fetchUpcoming(): Promise<UpcomingPost[]> {
  const { data } = await api.get<UpcomingPost[]>('/scheduler/upcoming', { params: { limit: 5 } });
  return data;
}

async function fetchInsights(): Promise<DailyInsight> {
  const { data } = await api.get<DailyInsight>('/analytics/insights/daily');
  return data;
}

function formatNumber(n: number | string): string {
  if (typeof n === 'string') return n;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function Delta({ value }: { value: number }): JSX.Element {
  const positive = value >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={clsx('flex items-center gap-1 text-xs font-medium', positive ? 'text-emerald-600' : 'text-brand-danger')}>
      <Icon size={14} />
      <span>{positive ? '+' : ''}{value.toFixed(1)}%</span>
    </div>
  );
}

interface SummaryCardProps {
  icon: typeof Users;
  label: string;
  value: number | string;
  delta: number;
  suffix?: string;
}

function StatCard({ icon: Icon, label, value, delta, suffix }: SummaryCardProps): JSX.Element {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-brand-primary/10 text-brand-primary">
          <Icon size={18} />
        </div>
        <Delta value={delta} />
      </div>
      <div className="text-2xl font-bold text-brand-ink">
        {formatNumber(value)}{suffix ?? ''}
      </div>
      <div className="text-xs text-brand-muted mt-1">{label}</div>
    </div>
  );
}

function SkeletonCard(): JSX.Element {
  return (
    <div className="card animate-pulse">
      <div className="h-8 w-8 bg-slate-200 rounded-lg mb-3" />
      <div className="h-6 w-20 bg-slate-200 rounded mb-2" />
      <div className="h-3 w-24 bg-slate-100 rounded" />
    </div>
  );
}

function EmptyOnboarding(): JSX.Element {
  const { t } = useTranslation();
  const nav = useNavigate();
  const steps = [
    { icon: Radio, key: 'step1' as const },
    { icon: BarChart3, key: 'step2' as const },
    { icon: Calendar, key: 'step3' as const },
  ];
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="card max-w-2xl w-full text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
          <Sparkles className="text-brand-primary" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-brand-ink mb-2">{t('dashboard.emptyTitle')}</h2>
        <p className="text-brand-muted mb-6">{t('dashboard.emptySubtitle')}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {steps.map(({ icon: Icon, key }, i) => (
            <div key={key} className="p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-primary text-white mx-auto mb-2 font-bold">
                {i + 1}
              </div>
              <Icon className="mx-auto text-brand-primary mb-2" size={20} />
              <div className="text-sm text-brand-ink">{t(`dashboard.${key}`)}</div>
            </div>
          ))}
        </div>
        <button onClick={() => nav('/onboarding')} className="btn-primary">
          {t('dashboard.startWizard')}
        </button>
      </div>
    </div>
  );
}

export function Dashboard(): JSX.Element {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('7d');

  const summary = useQuery({
    queryKey: ['analytics', 'summary', period],
    queryFn: () => fetchSummary(period),
    retry: false,
  });

  const upcoming = useQuery({
    queryKey: ['scheduler', 'upcoming'],
    queryFn: fetchUpcoming,
    retry: false,
    enabled: !!summary.data && summary.data.channels_count > 0,
  });

  const insights = useQuery({
    queryKey: ['analytics', 'insights', 'daily'],
    queryFn: fetchInsights,
    retry: false,
    enabled: !!summary.data && summary.data.channels_count > 0,
  });

  if (summary.isError) {
    return (
      <div className="p-6" data-testid="dashboard-empty">
        <EmptyOnboarding />
      </div>
    );
  }

  if (summary.isLoading) {
    return (
      <div className="p-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <div className="card h-64 animate-pulse" />
      </div>
    );
  }

  if (summary.data && summary.data.channels_count === 0) {
    return (
      <div className="p-6" data-testid="dashboard-empty">
        <EmptyOnboarding />
      </div>
    );
  }

  const s = summary.data;

  return (
    <div className="p-6" data-testid="dashboard-success">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-dark">{t('dashboard.title')}</h1>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm transition',
                period === p ? 'bg-brand-primary text-white' : 'text-brand-muted hover:bg-slate-50',
              )}
            >
              {t(`dashboard.period.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard icon={Users} label={t('dashboard.cards.subscribers')} value={s.subscribers.value} delta={s.subscribers.delta} />
          <StatCard icon={Eye} label={t('dashboard.cards.reach')} value={s.reach.value} delta={s.reach.delta} />
          <StatCard icon={TrendingUp} label={t('dashboard.cards.er')} value={s.er.value} delta={s.er.delta} suffix="%" />
          <StatCard icon={CalendarClock} label={t('dashboard.cards.scheduled')} value={s.scheduled.value} delta={s.scheduled.delta} />
          <StatCard icon={Bot} label={t('dashboard.cards.farm')} value={s.farm.value} delta={s.farm.delta} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-brand-ink mb-4">{t('dashboard.chartTitle')}</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s?.chart ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#607D8B" fontSize={12} />
                  <YAxis stroke="#607D8B" fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="subscribers" stroke="#0088CC" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-brand-ink mb-4">{t('dashboard.upcomingTitle')}</h3>
            {upcoming.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            ) : !upcoming.data || upcoming.data.length === 0 ? (
              <p className="text-brand-muted text-sm">{t('dashboard.upcomingEmpty')}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcoming.data.map((post) => (
                  <li key={post.id} className="py-3 flex items-center gap-3" data-testid="upcoming-post">
                    <div className="text-xs text-brand-muted w-24 shrink-0">
                      {dayjs(post.scheduled_at).format('DD MMM HH:mm')}
                    </div>
                    <div className="text-sm font-medium text-brand-primary w-32 shrink-0 truncate">
                      {post.channel_name}
                    </div>
                    <div className="text-sm text-brand-ink flex-1 truncate">{post.text_preview}</div>
                    <div className="flex gap-1 shrink-0">
                      <button className="p-1.5 rounded hover:bg-slate-100 text-brand-muted" aria-label={t('common.edit')}>
                        <Pencil size={14} />
                      </button>
                      <button className="p-1.5 rounded hover:bg-slate-100 text-brand-danger" aria-label={t('common.cancel')}>
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <h3 className="text-lg font-semibold text-brand-ink">{t('dashboard.insightsTitle')}</h3>
          {insights.isLoading ? (
            <>
              <div className="card h-24 animate-pulse" />
              <div className="card h-24 animate-pulse" />
              <div className="card h-24 animate-pulse" />
            </>
          ) : insights.data ? (
            <>
              <div className="card border-l-4 border-l-emerald-500">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-emerald-600" />
                  <h4 className="font-semibold text-sm text-brand-ink">{t('dashboard.insight.best')}</h4>
                </div>
                <div className="text-sm font-medium text-brand-ink">{insights.data.best_post.title}</div>
                <p className="text-xs text-brand-muted mt-1">{insights.data.best_post.description}</p>
              </div>
              <div className="card border-l-4 border-l-orange-500">
                <div className="flex items-center gap-2 mb-2">
                  <Flame size={16} className="text-orange-600" />
                  <h4 className="font-semibold text-sm text-brand-ink">{t('dashboard.insight.trend')}</h4>
                </div>
                <div className="text-sm font-medium text-brand-ink">{insights.data.trend.title}</div>
                <p className="text-xs text-brand-muted mt-1">{insights.data.trend.description}</p>
              </div>
              <div className="card border-l-4 border-l-brand-primary">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb size={16} className="text-brand-primary" />
                  <h4 className="font-semibold text-sm text-brand-ink">{t('dashboard.insight.recommendation')}</h4>
                </div>
                <div className="text-sm font-medium text-brand-ink">{insights.data.recommendation.title}</div>
                <p className="text-xs text-brand-muted mt-1">{insights.data.recommendation.description}</p>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
